import { CustomAxios } from '../../redux/axios';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const isValidUUID = (text: string): boolean => UUID_REGEX.test(text);

export const parseLocationInput = (input: string): string[] => {
  return input
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
};

export interface LocationSearchResult {
  LocationID: string;
  Name: string;
  Slug?: string;
  Active: boolean;
  [key: string]: any;
}

export interface SlugResolutionResult {
  original: string;
  locationId: string | null;
  isUuid: boolean;
  error?: string;
}

export const resolveLocationIdentifiers = async (
  identifiers: string[]
): Promise<SlugResolutionResult[]> => {
  const results: SlugResolutionResult[] = [];

  for (const identifier of identifiers) {
    if (isValidUUID(identifier)) {
      // It's already a UUID, no need to resolve
      results.push({
        original: identifier,
        locationId: identifier,
        isUuid: true,
      });
    } else {
      // It's a slug, need to resolve to UUID
      try {
        const response = await CustomAxios.get<{ data: LocationSearchResult[] }>(
          `/support/v1/locations/search/${encodeURIComponent(identifier)}`
        );

        const locations = response.data.data;

        if (!locations || locations.length === 0) {
          results.push({
            original: identifier,
            locationId: null,
            isUuid: false,
            error: 'Location not found',
          });
        } else if (locations.length > 1) {
          // Multiple matches - ambiguous, require user to be more specific
          const locationNames = locations
            .slice(0, 3)
            .map((l) => l.Name)
            .join(', ');
          results.push({
            original: identifier,
            locationId: null,
            isUuid: false,
            error: `Multiple locations found (${locations.length}): ${locationNames}${
              locations.length > 3 ? '...' : ''
            }. Please use UUID instead.`,
          });
        } else {
          // Single match - use it
          results.push({
            original: identifier,
            locationId: locations[0].LocationID,
            isUuid: false,
          });
        }
      } catch (error) {
        results.push({
          original: identifier,
          locationId: null,
          isUuid: false,
          error: error instanceof Error ? error.message : 'Failed to resolve slug',
        });
      }
    }
  }

  return results;
};

export const validateLocationIds = (
  locationIds: string[]
): { valid: string[]; invalid: string[] } => {
  const valid: string[] = [];
  const invalid: string[] = [];

  locationIds.forEach((id) => {
    if (isValidUUID(id)) {
      valid.push(id);
    } else {
      invalid.push(id);
    }
  });

  return { valid, invalid };
};
